import {
  AlertModalTypeEnum,
  CheckBolderIcon,
  CloseIcon,
  Flex,
  FlexColumn,
  PrimaryButton,
  SecondaryButton,
  SuccessIcon,
  TextInput,
  ValidationFunction,
  errorsToPropertyErrors,
  getErrors,
  password as passwordRule,
  requiredWithMessage,
  useAlertModal,
  useAsyncFunctionProgress,
  useAuth,
  useStyledTheme,
} from '@equifinance/shared';
import {
  ComponentProps, useCallback, useMemo, useState,
} from 'react';
import styled from 'styled-components';
import useChangePasswordApi, { ChangePasswordPayload } from '../../../api/useChangePasswordApi';
import { useBrokerContext } from '../../../contexts/broker';
import {
  DotIcon,
  PasswordRuleIcon, PasswordRuleItem, PasswordRuleLabel, PasswordRuleWrapper, PasswordSuffix, RowWrapper,
} from '../../../pages/ResetPassword';

const ChangePasswordContainer = styled(Flex)`${({ theme }) => `
  padding: ${theme.padding[5]};
  justify-content: center;
`}`;

const ChangePasswordContent = styled(FlexColumn)`${({ theme }) => `
  padding: ${theme.padding[5]};
  background-color: ${theme.colours.neutral[9]};
  width: 70%;
`}`;

const ChangePasswordBody = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[7]};
  padding: ${theme.padding[5]};
  align-self: center;
  background-color: ${theme.colours.neutral[9]};
  width: 50%;
`}`;

export const ChangePasswordHeader = styled.div`${({ theme }) => `
  background: ${theme.colours.neutral[8]};
  border-top-left-radius: ${theme.borderRadius[1]};
  border-top-right-radius: ${theme.borderRadius[1]};
  color: ${theme.colours.neutral[2]};
  padding: ${theme.padding[2]};
  font-size: ${theme.fontSize[2]};
  font-weight: ${theme.fontWeight.bold};
`}`;

interface ValidationType {
  [key: string]: string[];
}

const passwordRules: ValidationFunction[] = [...passwordRule];

type ChangePasswords = {
  existingPassword: string,
  newPassword: string,
  repeatPassword: string,
}

type PassNames = keyof ChangePasswords;

const customRequiredErrMessage = 'This field is required.';
const customRequiredValidation = requiredWithMessage(customRequiredErrMessage);

const changePasswordRules: {
  [key in PassNames]: ValidationFunction[];
} = {
  existingPassword: [customRequiredValidation],
  newPassword: [customRequiredValidation, ...passwordRules],
  repeatPassword: [customRequiredValidation],
};

function PasswordInput(props: ComponentProps<typeof TextInput>) {
  const [showPass, setShowPass] = useState(false);

  return (
    <TextInput
      {...props}
      type={showPass ? 'text' : 'password'}
      suffix={<PasswordSuffix isShow={showPass} />}
      onClickSuffix={() => setShowPass(!showPass)}
    />
  );
}

export default function ChangePasswordSection({ onCancel, onChangedPassword }: { onCancel: () => void, onChangedPassword: () => void }) {
  const { id, brokerDetails: { emailAddress } } = useBrokerContext();

  const { setAlertModalProps, showAlertModal, closeAlertModal } = useAlertModal();
  const theme = useStyledTheme();
  const { postChangePassword } = useChangePasswordApi();
  const { login, logout } = useAuth();
  const [resErrors, setResErrors] = useState<Record<string, string[]>>();

  const [passwords, setPasswords] = useState<ChangePasswords>({
    existingPassword: '',
    newPassword: '',
    repeatPassword: '',
  });

  const [validation, setValidation] = useState<ValidationType>({});

  const errors: Record<PassNames, string[]> = useMemo(() => {
    const { existingPassword, newPassword, repeatPassword } = passwords;
    const existingPassErrors = getErrors(existingPassword, changePasswordRules.existingPassword);
    const newPassErrors = getErrors(newPassword, changePasswordRules.newPassword);
    const repeatPassErrors = getErrors(repeatPassword, changePasswordRules.repeatPassword);

    if (newPassword && repeatPassword && newPassword !== repeatPassword) {
      repeatPassErrors.push('Password does not match.');
    }

    return {
      existingPassword: existingPassErrors,
      newPassword: newPassErrors,
      repeatPassword: repeatPassErrors,
    };
  }, [passwords]);

  const setPass = (passName: PassNames, value: string) => {
    setPasswords((prev) => ({ ...prev, [passName]: value }));
  };

  const ValidationIcon = useCallback((rule: string) => {
    const passwordErrors = validation.newPassword;

    if (!passwordErrors || passwordErrors?.includes(customRequiredErrMessage)) {
      return (<DotIcon />);
    }

    if (passwordErrors?.includes(rule)) {
      return (<CloseIcon fill={theme.colours.danger[1]} width={13} />);
    }

    return (<CheckBolderIcon fill={theme.colours.success[1]} width={12} height={10} />);
  }, [validation]);

  const newPasswordErrors = useMemo(() => {
    if (errors.newPassword.includes(customRequiredErrMessage)) {
      return [customRequiredErrMessage, ...(resErrors?.newPassword ?? [])];
    }

    return resErrors?.newPassword ?? [];
  }, [errors.newPassword, resErrors?.newPassword]);

  const onFieldChange = (field: PassNames, value: any) => {
    setPass(field, value);

    const validators = changePasswordRules[field] ?? [];
    const errors = getErrors(value, validators) ?? [];

    setValidation((prev) => ({
      ...prev,
      [field]: errors,
    }));
  };

  const showSuccessAlert = () => {
    setAlertModalProps({
      isOpen: true,
      cancelLabel: '',
      confirmLabel: 'OK',
      title: 'Password set',
      titleIconOverride: <SuccessIcon color={theme.colours.primary[1]} fill="white" />,
      onClose: () => {
        closeAlertModal();
      },
      onConfirm: () => {
        logout();
      },
      content: (
        <>
          <div>
            Your password has been successfully set. You will receive an email confirming the password reset.
          </div>
          <div>
            For security purposes, you will be logged out. Please log into the system again using your new password.
          </div>
        </>
      ),
    });

    showAlertModal();
  };

  const showFailedAlert = () => {
    setAlertModalProps({
      isOpen: true,
      cancelLabel: '',
      preventOutsideClose: true,
      hideClose: true,
      confirmLabel: 'OK',
      title: 'Password Not Set',
      type: AlertModalTypeEnum.WARNING_IRREVERSIBLE,
      onClose: () => {
        closeAlertModal();
      },
      onConfirm: () => {
        logout();
      },
      content: (
        <div>
          <p>
            We have been unable to update your password. For security purposes, you will be logged out.
            <br />
            <br />
            If this issue persists please contact your Equifinance System Administrator
          </p>
        </div>
      ),
    });

    showAlertModal();
  };

  const onConfirm = async () => {
    try {
      const changePasswordPayload: ChangePasswordPayload = {
        newPassword: passwords.newPassword,
        existingPassword: passwords.existingPassword,
      };

      const changePassRes = await postChangePassword(changePasswordPayload);

      const { errors } = changePassRes;
      const changePassError = errors?.[id] ?? [];

      // check errors
      if (changePassError.length) {
        const errorsPerField = errorsToPropertyErrors(changePassError);

        setResErrors({
          existingPassword: errorsPerField?.existingPassword ?? [],
          newPassword: errorsPerField?.newPassword ?? [],
        });

        return;
      }

      await login(emailAddress, passwords.newPassword);

      showSuccessAlert();
      onChangedPassword();
    } catch (e) {
      showFailedAlert();
      onChangedPassword();
    }
  };

  const { inProgress: confirmInProgress, execute: executeConfirm } = useAsyncFunctionProgress(onConfirm);

  return (
    <ChangePasswordContainer>
      <ChangePasswordContent>
        <ChangePasswordHeader>Change your password</ChangePasswordHeader>

        <ChangePasswordBody>
          <RowWrapper>
            <PasswordInput
              id="existingPassword"
              label="Existing password"
              value={passwords.existingPassword ?? ''}
              onChange={(value: string) => onFieldChange('existingPassword', value)}
              required
              errors={[...errors.existingPassword, ...resErrors?.existingPassword ?? []]}
            />
          </RowWrapper>

          <RowWrapper>
            <PasswordInput
              id="newPassword"
              label="New password"
              value={passwords.newPassword ?? ''}
              onChange={(value: string) => onFieldChange('newPassword', value)}
              required
              errors={newPasswordErrors}
            />

            <PasswordRuleWrapper>
              <PasswordRuleLabel>
                Password must:
              </PasswordRuleLabel>
              {passwordRules.map((rule) => (
                <PasswordRuleItem key={`${rule.error}-text`}>
                  <PasswordRuleIcon>{ValidationIcon(rule.error)}</PasswordRuleIcon>
                  <PasswordRuleLabel>{rule.error}</PasswordRuleLabel>
                </PasswordRuleItem>
              ))}
            </PasswordRuleWrapper>
          </RowWrapper>
          <RowWrapper>
            <PasswordInput
              id="repeatPassword"
              label="Re-enter your new password"
              value={passwords.repeatPassword ?? ''}
              onChange={(value: string) => onFieldChange('repeatPassword', value)}
              required
              errors={errors.repeatPassword}
            />
          </RowWrapper>
          <PrimaryButton
            id="confirmed-btn"
            onClick={executeConfirm}
            disabled={
              errors?.existingPassword?.length
              || errors?.newPassword?.length
              || errors?.repeatPassword?.length
              || confirmInProgress
            }
          >
            Confirm
          </PrimaryButton>

          <SecondaryButton
            id="cancel-btn"
            onClick={onCancel}
          >
            Cancel
          </SecondaryButton>
        </ChangePasswordBody>
      </ChangePasswordContent>
    </ChangePasswordContainer>
  );
}
